import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import TriCTA from "../components/TriCTA";

const STAGES = [
  {
    code: "01",
    title: "Botswana Cattle",
    actor: "National Herd · 4M+ head",
    body: "One of Africa's largest and most consistent national herds. Traceable, ethically managed, EU-grade hides at scale.",
    value: "Base input",
  },
  {
    code: "02",
    title: "Raw Hide",
    actor: "BMC supply pipeline · 300,000+ slaughtered annually",
    body: "Currently exported with minimal processing. The base material of the entire value chain. Collection, grading and preservation handled at source.",
    value: "$30 - $50",
  },
  {
    code: "03",
    title: "Wet Blue Leather",
    actor: "LRG Tannery · Lobatse SEZ",
    body: "Chrome-tanned to preserve and stabilize the hide. First industrial processing step. Italprogetti-engineered tannery infrastructure with CETP effluent treatment.",
    value: "$80 - $120",
  },
  {
    code: "04",
    title: "Finished Leather",
    actor: "Italian-grade crust & finished",
    body: "Dyed, textured, and finished to international quality specifications. Italian technical expertise (Italprogetti, White Line) delivers globally competitive material.",
    value: "$150 - $300+",
  },
  {
    code: "05",
    title: "Luxury Goods Manufacturing",
    actor: "Footwear · Bags · Accessories · Automotive",
    body: "Angelo Coppola partnership for footwear (350 pairs/day capacity). Private label, OEM and own-brand production across bags, belts, shoes and automotive interiors.",
    value: "$500 - $5,000+",
  },
  {
    code: "06",
    title: "Export Markets",
    actor: "US · EU · UK · SACU · SADC · AfCFTA",
    body: "Six export markets targeted. AGOA, EU-SADC EPA and UK-SACUM qualification frameworks already mapped. Full value capture and sovereign wealth creation.",
    value: "Full capture",
  },
];

const VALUE_ADDITION = [
  ["Raw Hide", "$30 - $50"],
  ["Wet Blue", "$80 - $120"],
  ["Finished", "$150 - $300+"],
  ["Luxury Goods", "$500 - $5K+"],
];

export default function SupplyChain() {
  return (
    <div data-testid="page-supply-chain" className="bg-[#F9F9F6]">
      <section className="pt-32 md:pt-40 pb-16 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow">Supply Chain</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight mt-6">
              The full leather <span className="italic text-[#5C2018]">value chain.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#1A1A1A] max-w-2xl leading-relaxed">
              Botswana cattle to global luxury. Six integrated stages, one operating platform, executed inside the SEZA-aligned Lobatse facility.
            </p>
          </div>
        </div>
      </section>

      {/* Vertical stage flow */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pb-24" data-testid="supply-chain-stages">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-3 md:sticky md:top-32 self-start">
            <p className="eyebrow">Six stages</p>
            <h2 className="font-serif text-3xl tracking-tight mt-4">Cattle → Luxury.</h2>
            <p className="text-sm text-[#666666] mt-4 leading-relaxed">Each stage is owned, tracked, and executed inside the LRG operating platform.</p>
          </div>
          <div className="md:col-span-9">
            <div className="border-l-2 border-[#1A1A1A]">
              {STAGES.map((s, i) => (
                <div key={s.code} data-testid={`supply-stage-${s.code}`} className="relative pl-10 md:pl-14 pb-12 last:pb-0">
                  <div className="absolute left-[-9px] top-0 w-4 h-4 bg-[#5C2018]" />
                  <div className="absolute left-[-15px] top-[-2px] w-7 h-7 border border-[#1A1A1A]" />
                  <div className="flex items-baseline justify-between gap-6 flex-wrap">
                    <p className="font-serif text-[#5C2018] text-2xl">{s.code}</p>
                    <p className="font-serif text-2xl md:text-3xl text-[#5C2018] tracking-tight">{s.value}</p>
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 leading-tight">{s.title}</h3>
                  <p className="eyebrow mt-3">{s.actor}</p>
                  <p className="text-base text-[#1A1A1A] mt-4 leading-relaxed max-w-2xl">{s.body}</p>
                  {i < STAGES.length - 1 && (
                    <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#666666]">
                      <ChevronDown size={14} className="text-[#5C2018]" /> next stage
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value addition */}
      <section className="bg-[#1A1A1A] text-[#F9F9F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow text-[#A8A8A0]">Value Addition</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4 leading-tight">Each stage multiplies value.</h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
              <p className="text-sm text-[#C7C7BF] leading-relaxed">
                Indicative per-hide value capture across the chain. The platform captures the full ladder. Not just the bottom step.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#333330]">
            {VALUE_ADDITION.map(([k, v]) => (
              <div key={k} className="border-r border-b border-[#333330] p-6 md:p-8">
                <p className="eyebrow text-[#A8A8A0]">{k}</p>
                <p className="font-serif text-3xl md:text-4xl text-[#E5C4A1] tracking-tight mt-4">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TriCTA kicker="Plug into the chain" heading={<>Find your stage. <span className="italic text-[#E5C4A1]">Engage.</span></>} />

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20">
        <div className="border-t border-[#1A1A1A] pt-10 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Ready to review the financial model?</h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/financial-model" data-testid="supply-to-model" className="inline-flex items-center gap-3 px-7 py-4 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A]">
              Financial Model <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
